import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { NotificationBellMenu } from '@mlm/ui';
import {
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../lib/api';

/**
 * Creator Studio bell (LMS Phase 2). Same query keys, poll and invalidation as
 * the member bell — only the navigation target differs, because every creator
 * `metadata.path` is a creator-SPA route.
 *
 * Never gated on approval status: a PENDING creator is exactly who needs the
 * approved/rejected bell, and `/me/notifications` needs no creator permission.
 */
export function NotificationsBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const unreadQ = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: getUnreadCount,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const recentQ = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => listNotifications({ limit: 10 }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markOne = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  return (
    <NotificationBellMenu
      items={recentQ.data?.items ?? []}
      unreadCount={unreadQ.data?.count ?? 0}
      onMarkRead={(id) => markOne.mutate(id)}
      onMarkAll={() => markAll.mutate()}
      markAllPending={markAll.isPending}
      onOpen={(path) => {
        // A `creator_approved` click should un-grey the gated nav immediately;
        // `creator-me` is otherwise cached and the sidebar would lag.
        qc.invalidateQueries({ queryKey: ['creator-me'] });
        navigate(path);
      }}
      seeAllHref="/notifications"
      align="start"
      compact
    />
  );
}
